import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import AcademicSessionForm from './AcademicSessionForm';
import { useActiveAcademicSession } from '../../../hooks/useActiveAcademicSession';

interface AcademicSession {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const AcademicSessionList: React.FC = () => {
  const [sessions, setSessions] = useState<AcademicSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  
  // Dialog States
  const [confirmOpen, setConfirmOpen] = useState(false); // Activate
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false); // Deactivate
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // Delete
  
  const [selectedSession, setSelectedSession] = useState<AcademicSession | null>(null);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { refreshSession: refreshGlobalActive } = useActiveAcademicSession();

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/system-settings/academic-sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Toggle Handler
  const handleToggleStatus = (session: AcademicSession) => {
    setSelectedSession(session);
    if (session.isActive) {
      setDeactivateConfirmOpen(true);
    } else {
      setConfirmOpen(true);
    }
  };

  // Activate
  const handleConfirmActivate = async () => {
    if (!selectedSession) return;
    try {
      await axios.patch(`/system-settings/academic-sessions/${selectedSession.id}/activate`);
      setConfirmOpen(false);
      showSnackbar('Session activated successfully', 'success');
      fetchSessions();
      refreshGlobalActive();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to activate session', 'error');
    }
  };

  // Deactivate
  const handleConfirmDeactivate = async () => {
    if (!selectedSession) return;
    try {
      await axios.patch(`/system-settings/academic-sessions/${selectedSession.id}/deactivate`);
      setDeactivateConfirmOpen(false);
      showSnackbar('Session deactivated successfully', 'success');
      fetchSessions();
      refreshGlobalActive();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to deactivate session', 'error');
    }
  };

  // Delete
  const handleDeleteClick = (session: AcademicSession) => {
    if (session.isActive) return;
    setSelectedSession(session);
    setDeleteConfirmOpen(true);
  };

  const handleEditClick = (session: AcademicSession) => {
    setSelectedSession(session);
    setOpenForm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSession) return;
    try {
      const response = await axios.delete(
        `/system-settings/academic-sessions/${selectedSession.id}`,
      );
      setDeleteConfirmOpen(false);
      if (response.data.status === "DEACTIVATED") {
        showSnackbar(
          `Session has data (Fee Structures or Reports) and cannot be deleted. It has been deactivated.`,
          'success',
        );
      } else {
        showSnackbar(response.data.message, "success");
      }
      fetchSessions();
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Failed to delete session",
        "error",
      );
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Academic Sessions</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedSession(null);
            setOpenForm(true);
          }}
        >
          New Session
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Session Name</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">{session.name}</Typography>
                </TableCell>
                <TableCell>
                  {new Date(session.startDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {new Date(session.endDate).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={session.isActive}
                    onChange={() => handleToggleStatus(session)}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit Session">
                    <IconButton
                      color="primary"
                      onClick={() => handleEditClick(session)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {!session.isActive && (
                    <Tooltip title="Delete (if unused)">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(session)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sessions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No academic sessions found. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AcademicSessionForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSuccess={() => {
          setOpenForm(false);
          fetchSessions();
        }}
        initialValues={selectedSession || undefined}
      />

      {/* Activate Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Activate Academic Session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate <strong>{selectedSession?.name}</strong>?
            <br />
            This will automatically deactivate the currently active session.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmActivate} color="primary" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Dialog */}
      <Dialog open={deactivateConfirmOpen} onClose={() => setDeactivateConfirmOpen(false)}>
        <DialogTitle>Deactivate Academic Session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate <strong>{selectedSession?.name}</strong>?
            <br />
            The system will have no active academic session.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDeactivate} color="warning" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Academic Session?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{selectedSession?.name}</strong>?
            <br />
            If this session is in use (students, fees, etc.), it will be{" "}
            <strong>deactivated</strong> instead of deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AcademicSessionList;